import { Commission, Invoice } from '../../../providers/api/dtos';
import { InvoiceCommissionValueType } from '../../types';
import { InvoiceCommissionFormModel } from './InvoiceCommissionForm';
import { roundHalfUp } from '../../../utils';

export function getCommissionValue(
  fromValueType: InvoiceCommissionValueType,
  toValueType: InvoiceCommissionValueType,
  value: string,
  invoiceValue: number,
): number {
  if (invoiceValue === 0) {
    throw new Error('Invoice value cannot be zero');
  }

  if (fromValueType === toValueType) return parseFloat(value);

  // Converting from PERCENTAGE to FIXED
  if (
    fromValueType === InvoiceCommissionValueType.PERCENTAGE
    && toValueType === InvoiceCommissionValueType.FIXED
  ) {
    const fixedValue = parseFloat(value) * (invoiceValue / 100);
    return roundHalfUp(fixedValue, 2);
  }

  // Converting from FIXED to PERCENTAGE
  const percentageValue = (parseFloat(value) * 100) / invoiceValue;
  const normalizedValue = Math.min(Math.max(roundHalfUp(percentageValue, 2), 0), 100);

  return normalizedValue;
}

const calculateCommissionValues = (
  valueType: InvoiceCommissionFormModel['valueType'],
  value: InvoiceCommissionFormModel['value'],
  invoicePaidOrTotalValue: number,
) => {
  const newValue = getCommissionValue(valueType, InvoiceCommissionValueType.FIXED, value, invoicePaidOrTotalValue);
  const newPercentage = getCommissionValue(valueType, InvoiceCommissionValueType.PERCENTAGE, value, invoicePaidOrTotalValue);

  return { newValue, newPercentage };
};

export const commissionToCommissionFormModel = (
  commission: Commission,
  valueType: InvoiceCommissionValueType = InvoiceCommissionValueType.PERCENTAGE,
): InvoiceCommissionFormModel => ({
  agentId: commission.agentId,
  value: valueType === InvoiceCommissionValueType.PERCENTAGE ? commission.percentage.toString() : commission.amount.toString(),
  valueType,
  type: commission.type,
});

export const commissionFormModelToCommission = (
  {
    valueType, value, agentId, type,
  }: InvoiceCommissionFormModel,
  invoice: Invoice,
): Commission => {
  const invoicePaidOrTotalValue = invoice.paidValue ?? invoice.value;

  const { newValue, newPercentage } = calculateCommissionValues(
    valueType,
    value,
    invoicePaidOrTotalValue,
  );

  return {
    agentId: agentId!,
    type,
    amount: newValue,
    percentage: newPercentage,
  };
};
